import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { api } from "../api/client.ts"

export type ApiQuery<T> = {
  /** 아직 한 번도 못 받았으면 null. 다시 읽는 동안에는 **직전 값을 그대로 들고 있는다**. */
  data: T | null
  error: unknown
  /** 요청이 나가 있는 동안 true. 첫 로딩과 새로고침을 구별하려면 `data` 와 함께 본다. */
  loading: boolean
  /** 같은 주소를 다시 읽는다. 무언가 고친 뒤에 부른다. */
  reload: () => Promise<void>
}

/**
 * GET 한 번을 화면 상태로 바꾼다.
 *
 * 화면마다 손으로 적던 `useState(null)` + `useEffect` + `.catch(setError)` 를 대신한다.
 * 손으로 적은 것들에는 **요청 취소가 없어서, 늦게 온 옛 응답이 새 응답을 덮어썼다**
 * (검색어나 필터를 빠르게 바꿀 때). 여기서는 마지막 요청의 결과만 반영한다.
 *
 * `path` 가 null 이면 요청하지 않는다 — "관리자일 때만 읽는다" 같은 조건부 조회에 쓴다.
 * 주소가 곧 캐시 열쇠이므로, 조건이 바뀌면 주소를 바꿔 주면 알아서 다시 읽는다.
 */
export function useApiQuery<T>(path: string | null): ApiQuery<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(path !== null)

  // 지금 유효한 요청의 번호. 취소되거나 뒤늦게 온 응답은 이 번호가 다르다.
  const ticketRef = useRef(0)
  const pathRef = useRef(path)

  const load = useCallback(async (target: string | null): Promise<void> => {
    const ticket = ++ticketRef.current
    if (target === null) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await api.get<T>(target)
      if (ticket !== ticketRef.current) return
      setData(result)
    } catch (err) {
      if (ticket !== ticketRef.current) return
      setError(err)
    } finally {
      if (ticket === ticketRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    pathRef.current = path
    void load(path)
    // 주소가 바뀌거나 화면이 사라지면 이전 요청의 결과를 버린다.
    return () => {
      // DOM 을 가리키는 ref 가 아니라 번호표다. 정리할 때의 "지금 값"을 올리는 것이 목적이다.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ticketRef.current++
    }
  }, [load, path])

  const reload = useCallback(() => load(pathRef.current), [load])

  // 값이 그대로면 같은 객체를 돌려준다 — 부르는 쪽이 의존성 배열에 그대로 넣을 수 있게.
  return useMemo(() => ({ data, error, loading, reload }), [data, error, loading, reload])
}
